import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { ArrowRight, BadgePercent, CalendarCheck, Truck, ShieldCheck, Snowflake, Clock } from "lucide-react";

const CODE = "WINTER10";

export default function WinterSpecial() {
  const [vehicles, setVehicles] = useState([]);
  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles((r.data || []).filter((v) => v.active !== false).slice(0, 4))).catch(() => {});
  }, []);

  useSEO({
    title: "Nassau Car Rental Winter Deal — 10% Off All Rentals | Easy Drive",
    description: "Winter special in Nassau, Bahamas: 10% off every car rental with code WINTER10. Free hotel, villa & airport delivery on New Providence and Paradise Island. Book direct and save.",
    path: "/winter-special",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Offer",
      name: "Winter Special — 10% off all car rentals in Nassau, Bahamas",
      description: "10% off every rental with promo code WINTER10 at Easy Drive Car Rental, Nassau. Free island delivery included.",
      url: "https://easydrivecarrental.com/winter-special",
      seller: { "@type": "Organization", name: "Easy Drive Car Rental", url: "https://easydrivecarrental.com/" },
      areaServed: { "@type": "Place", name: "Nassau, Bahamas" },
      category: "Car rental",
      discountCode: CODE,
    },
  });

  return (
    <div data-testid="winter-special-page" className="pt-36 pb-24">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary flex items-center justify-center gap-2"><Snowflake size={14} /> Winter Special</p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight">
          Winter in Nassau.<br /><span className="italic gold-text">10% off</span> every rental.
        </h1>
        <p className="mt-6 text-muted-foreground leading-relaxed font-ui max-w-2xl mx-auto">
          Trade the cold for 27°C and turquoise water. Book any vehicle this winter with code
          <span data-testid="winter-code-chip" className="mx-2 inline-block rounded-full bg-primary text-primary-foreground font-ui font-bold px-4 py-1 tracking-widest">{CODE}</span>
          and take 10% off your entire rental — every vehicle, every day.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to="/fleet">
            <Button data-testid="winter-browse-fleet" size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-9 h-12">
              Browse the fleet <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
          <p className="text-xs text-muted-foreground font-ui flex items-center gap-1.5"><Clock size={13} /> Limited-time seasonal rate — book early for Christmas & New Year</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 mt-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: BadgePercent, t: "10% off, no fine print", d: "The discount applies to the whole rental — daily rate, extras, everything. Enter WINTER10 at checkout and watch the price drop." },
            { icon: Truck, t: "Free island delivery", d: "Hotel, villa, marina or airport — we bring the car anywhere on New Providence & Paradise Island, and collect it when you're done." },
            { icon: ShieldCheck, t: "Zero-deposit peace of mind", d: "Basic insurance included, a simple $150 refundable hold, and optional SLI liability cover from $7.99/day with a $500 deductible." },
          ].map(({ icon: Icon, t, d }, i) => (
            <div key={i} data-testid={`winter-perk-${i + 1}`} className="bg-card border border-border rounded-2xl p-6">
              <span className="w-11 h-11 rounded-full border border-primary/30 flex items-center justify-center"><Icon size={18} className="gold-text" /></span>
              <p className="mt-4 font-ui font-semibold text-sm">{t}</p>
              <p className="mt-1.5 text-xs text-muted-foreground font-ui leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>

      {vehicles.length > 0 && (
        <div className="max-w-6xl mx-auto px-6 lg:px-10 mt-16">
          <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Winter rates with {CODE}</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {vehicles.map((v) => (
              <Link key={v.id} data-testid={`winter-vehicle-${v.id}`} to={`/book/${v.id}?coupon=${CODE}`} className="group bg-card border border-border rounded-2xl overflow-hidden transition-colors duration-200 hover:border-primary/40">
                <div className="aspect-[4/3] bg-black/[0.03] overflow-hidden">
                  <img src={v.image_url} alt={v.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                </div>
                <div className="p-4">
                  <p className="font-ui font-semibold text-sm leading-snug">{v.name}</p>
                  <p className="mt-1.5 text-xs font-ui">
                    <span className="text-muted-foreground line-through">${Number(v.price_per_day).toFixed(2)}</span>
                    <span className="ml-2 text-primary font-bold">${(Number(v.price_per_day) * 0.9).toFixed(2)}</span>
                    <span className="text-muted-foreground"> /day</span>
                  </p>
                  <p className="mt-2 text-xs font-ui font-semibold text-primary flex items-center gap-1">Book with {CODE} <ArrowRight size={12} /></p>
                </div>
              </Link>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground font-ui">Each vehicle link pre-applies the code — you'll see the 10% discount in your price summary before you pay.</p>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-6 lg:px-10 mt-16">
        <div className="bg-card border border-border rounded-2xl p-8">
          <p className="font-ui font-semibold text-sm flex items-center gap-2"><CalendarCheck size={16} className="gold-text" /> The details</p>
          <ul className="mt-3 text-xs text-muted-foreground font-ui leading-relaxed space-y-1.5 list-disc pl-4">
            <li>Enter code {CODE} in the coupon field at checkout, or use any vehicle link above to apply it automatically.</li>
            <li>Applies to the full rental total: daily rate and add-ons. Valid on new bookings for winter travel dates while the promotion runs.</li>
            <li>Stacks with the automatic 10% weekly discount only where shown in your price summary; loyalty members keep their tier perks.</li>
            <li>Free cancellation up to 24 hours before pickup. Free delivery & collection across New Providence and Paradise Island.</li>
          </ul>
          <div className="mt-6">
            <Link to="/fleet">
              <Button data-testid="winter-cta-bottom" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-8 h-11">
                Claim 10% off — pick your vehicle <ArrowRight size={15} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
